import { useState } from 'react'

import {
  Box,
  Button,
  Icon,
  Text,
  Tooltip,
  Touchable,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { F0TagRaw } from '@factorialco/f0-react'
import { RichTextDisplay } from '@factorialco/f0-react/dist/experimental'
import { EyeVisible } from '@factorialco/f0-react/icons/app'
import {
  AtsFeedbackDetailFragment,
  EvaluationFormListItemFragment,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { AttachedAtsFile } from 'modules/ats/components/AttachedAtsFile'
import { FeedbackAuthor } from 'modules/ats/components/Feedbacks/FeedbackAuthor'
import { RatingSelector } from 'modules/ats/components/Feedbacks/RatingSelector'
import { ScoreRating } from 'modules/ats/components/ScoreRating'
import { colorForRating } from 'modules/ats/components/ScoreRating'
import { useOnFeedbackDelete } from 'modules/ats/lib/utils/feedbacks'

interface Props {
  feedback: AtsFeedbackDetailFragment
  evaluationForm?: EvaluationFormListItemFragment
  restrictedCommentsAvailable?: boolean
  onEdit: () => void
}

export const EvaluationFormDisplay = ({
  feedback,
  evaluationForm,
  onEdit,
  restrictedCommentsAvailable,
}: Props) => {
  const { isDeleting, onFeedbackDelete } = useOnFeedbackDelete(feedback)
  const [showAnswers, toggleShowAnswers] = useState(false)
  const [deleteModalOpen, toggleDeleteModalOpen] = useToggle(false)
  const [showRestrictedVisibilityTooltip, toggleRestrictedVisibilityTooltip] = useToggle(false)
  const {
    access,
    attachments,
    rating,
    description: summary,
    evaluationFormAnswers,
    visibility,
  } = feedback
  const {
    access: { id: accessId },
  } = useCurrentSession()
  const isFeedbacksAuthor = access?.id === accessId
  const questions = evaluationForm && evaluationForm.questions
  const hasRestrictedVisibility = visibility === 'restricted'

  return (
    <Box width="full" gap="s8">
      <Box gap="s8">
        <Box flexDirection="row" alignItems="center" justifyContent="spaceBetween">
          <FeedbackAuthor feedback={feedback} hideRating />
          <Box flexDirection="row" alignItems="center" gap="s8">
            {hasRestrictedVisibility && restrictedCommentsAvailable && (
              <Tooltip.Alt
                open={showRestrictedVisibilityTooltip}
                toggle={toggleRestrictedVisibilityTooltip}
                label={i18n.t('ats.feedbacks.restricted_visibility')}
                attachment="bottom center"
              >
                <F0TagRaw
                  text={i18n.t('ats.feedbacks.restricted_visibility')}
                  icon={EyeVisible}
                  onlyIcon
                />
              </Tooltip.Alt>
            )}

            {isFeedbacksAuthor && (
              <PopoverMenu type="list">
                <PopoverMenu.Trigger>
                  {/* TODO: Add aria label */}
                  <Button.Decorative type="secondary" iconName="ellipsis" />
                </PopoverMenu.Trigger>
                <PopoverMenu.Content side="bottom" align="center" disablePortal>
                  <PopoverMenu.TouchableOption title={i18n.t('common.edit')} onClick={onEdit} />
                  <PopoverMenu.TouchableOption
                    danger
                    title={i18n.t('common.destroy')}
                    onClick={toggleDeleteModalOpen(true)}
                  />
                </PopoverMenu.Content>
              </PopoverMenu>
            )}
          </Box>
        </Box>

        <Box flexDirection="row" gap="s16">
          <Box flexDirection="row" gap="s8">
            <ScoreRating rating={rating} />
            {rating && <Icon.Medium color={colorForRating(rating)} icon="star" />}
          </Box>
          <Text.Normal weight="medium" color="grey1000">
            {evaluationForm?.name}
          </Text.Normal>
        </Box>
        {summary && <RichTextDisplay content={summary} />}

        {attachments.length > 0 && (
          <Box flexDirection="row" gap="s16" flexWrap="wrap">
            {attachments.map((attachment) => (
              <AttachedAtsFile
                key={attachment.id}
                filename={attachment.filename}
                url={attachment.url}
                fileId={attachment.id}
              />
            ))}
          </Box>
        )}
      </Box>

      {evaluationFormAnswers && showAnswers ? (
        <>
          <Box
            border={{
              all: {
                width: 's1',
                style: 'solid',
                color: 'grey400',
              },
            }}
            borderRadius={{ all: 'abs008' }}
            background="grey000White"
            gapDivider
          >
            {questions?.map((question) => {
              const answer = evaluationFormAnswers?.find((a) => a.questionId === question.id)

              return (
                <Box key={question.id} padding="s16" gap="s16">
                  <Box gap="s8">
                    <Text.Normal color="grey1000" weight="medium">
                      {question.text}
                    </Text.Normal>
                    <Text.Normal color="grey1000">{question.description} </Text.Normal>
                  </Box>
                  <RatingSelector value={answer?.score} readonly />
                  {answer?.note && (
                    <Box
                      border={{ all: { color: 'grey400', style: 'solid', width: 's2' } }}
                      borderRadius={{ all: 'abs008' }}
                      background="grey200"
                      paddingX="s16"
                      paddingY="s8"
                      minHeight="s64"
                    >
                      <RichTextDisplay content={answer.note} />
                    </Box>
                  )}
                </Box>
              )
            })}
          </Box>
          <Touchable onClick={() => toggleShowAnswers(false)}>
            <Text.Normal weight="medium" color="viridian1200" size="200" underlined>
              {i18n.t('ats.application_details.evaluations.show_less')}
            </Text.Normal>
          </Touchable>
        </>
      ) : (
        <Touchable onClick={() => toggleShowAnswers(true)}>
          <Text.Normal weight="medium" color="viridian1200" size="200" underlined>
            {i18n.t('ats.application_details.evaluations.show_more')}
          </Text.Normal>
        </Touchable>
      )}

      {deleteModalOpen && (
        <Modal
          illustration="network-group"
          title={i18n.t('ats.feedbacks.delete_feedback')}
          description={i18n.t('ats.feedbacks.confirm_delete')}
          onClose={toggleDeleteModalOpen(false)}
          onSubmit={onFeedbackDelete}
          footer={
            <Button type="negative" disabled={isDeleting} submit>
              {i18n.t('ats.feedbacks.delete_feedback')}
            </Button>
          }
        />
      )}
    </Box>
  )
}
